import httpClient from "../../../../../api/httpClient";
import { withoutUncodedCharges } from "./chargeCodeGuards";
import Cookies from "js-cookie";
import notify from "../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { ensureArray, normalizeRateShoppingResults } from "./rateShoppingUtils";

const normalize = (val) => (val ?? "").toString().trim().toUpperCase();

// Match the address string used by RenderShipmentModals/RatesShoppingModal
const formatAddress = (stop) =>
  `${stop?.addressLine1 || ""}${stop?.addressLine2 || ""}, ${stop?.city || ""}, ${
    stop?.state || ""
  }, ${stop?.zipCode || ""}, ${stop?.country || ""}`;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
};

const extractCarrierCode = (result) => {
  const upper = (v) => (v ?? "").toString().toUpperCase();
  if (Array.isArray(result?.routeCarrierCodes) && result.routeCarrierCodes.length) {
    return upper(result.routeCarrierCodes[0]);
  }
  if (Array.isArray(result?.rateCarrierCodes) && result.rateCarrierCodes.length) {
    return upper(result.rateCarrierCodes[0]);
  }
  const seg = result?.pathSegments?.[0];
  if (seg?.routeCarrierCode) return upper(seg.routeCarrierCode);
  const pickBreakCode = (arr) =>
    ensureArray(arr).find((b) => b?.rateCarrierCode)?.rateCarrierCode;
  const rr = seg?.routeRate;
  const fromBreakdowns =
    pickBreakCode(rr?.rateBreakdown) ||
    pickBreakCode(rr?.accBreakdown) ||
    pickBreakCode(rr?.adjustedBreakdown);
  return upper(fromBreakdowns);
};

const injectFlatBaseIfMissing = (adjustedList = [], rateList = []) => {
  const normalized = (s) => (s ?? "").toString().toUpperCase();
  const adjusted = ensureArray(adjustedList);
  const rates = ensureArray(rateList);
  const hasBase = adjusted.some((b) => normalized(b.type) === "BASE");
  if (hasBase) return [...adjusted];

  const flatBase = rates.find(
    (b) => normalized(b.type) === "BASE" && normalized(b.basis) === "FLAT"
  );
  return flatBase ? [...adjusted, flatBase] : [...adjusted];
};

// Rate-shops the shipment (pickup date, miles, stops) and returns the contract breakdown for its
// assigned carrier: `breakdown` holds BASE/FSC (adjusted when present, else raw, with a flat BASE
// injected if missing) and `accBreakdown` the accessorials. Returns null when the assigned carrier
// isn't among the results. Shared by "Refresh Rates" and the Charge Details rate/basis lookup
// (LGX-1136) so both resolve the same lane + carrier + date.
export const fetchAssignedCarrierRates = async ({ shipmentDetails, officeCode, authToken }) => {
  const stops = [...(shipmentDetails?.shipmentStops || [])].sort(
    (a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0)
  );
  const originStop = stops[0];
  const destinationStop = stops[stops.length - 1];

  const fetchWithRetry = async (attempt = 0) => {
    const params = {
      origin: originStop?.stopName || "Origin",
      destination: destinationStop?.stopName || "Destination",
      originType: "GEOGRAPHY",
      destinationType: "GEOGRAPHY",
      shipmentNumber: shipmentDetails?.shipmentNumber,
      operationDate: shipmentDetails?.pickupDate
        ? formatDate(shipmentDetails.pickupDate)
        : undefined,
      totalMiles: shipmentDetails?.totalMiles ?? undefined,
      originAddress: attempt === 0 ? formatAddress(originStop) : null,
      destinationAddress: attempt === 0 ? formatAddress(destinationStop) : null,
      paymentMethod: shipmentDetails?.paymentMethod?.toUpperCase() || "PREPAID",
    };

    try {
      const res = await httpClient.post(
        `/api/organizations/${officeCode}/rate-shopping/all-paths`,
        {},
        {
          params,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "Office-Code": officeCode,
          },
        }
      );
      return normalizeRateShoppingResults(res?.data);
    } catch (err) {
      if (attempt < 2) {
        // Mirror modal: retry with exponential backoff, omit addresses after first try
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
        return fetchWithRetry(attempt + 1);
      }
      throw err;
    }
  };

  const results = await fetchWithRetry();
  const assignedCode = normalize(
    shipmentDetails?.carrierOfficeCode?.partyOfficeCode || shipmentDetails?.carrierCode
  );
  const assignedName = normalize(shipmentDetails?.carrierName);

  const matchingResult = results.find((r) => {
    const code = normalize(extractCarrierCode(r));
    const name = normalize(
      (Array.isArray(r?.rateCarrierCodes) && r.rateCarrierCodes[0]) ||
        (Array.isArray(r?.routeCarrierCodes) && r.routeCarrierCodes[0]) ||
        ""
    );
    return (assignedCode && code === assignedCode) || (assignedName && name === assignedName);
  });
  if (!matchingResult) return null;

  const breakdown = ensureArray(matchingResult?.pathSegments).flatMap((seg) => {
    const adjusted = ensureArray(seg.routeRate?.adjustedBreakdown);
    const rate = ensureArray(seg.routeRate?.rateBreakdown);
    const source = adjusted.length ? adjusted : rate;
    return injectFlatBaseIfMissing(source, rate);
  });
  const accBreakdown = ensureArray(
    matchingResult?.pathSegments?.[0]?.routeRate?.accBreakdown
  );
  return { breakdown, accBreakdown };
};

export const refreshRatesAndUpdateCharges = async ({
  shipmentDetails,
  officeCode,
  isRefreshingRates,
  setIsRefreshingRates,
  setShipmentDetails,
  // Bulk callers (e.g. the Shipment grid's "Refresh Rates" multi-select action) refresh many
  // shipments in one pass and show their own single aggregated toast -- silent suppresses this
  // function's own per-shipment notify calls, and the return value ({ success, reason }) is
  // what lets the caller build that aggregate instead.
  silent = false,
}) => {
  if (isRefreshingRates || !shipmentDetails) return { success: false, reason: "Invalid state" };

  const stops = [...(shipmentDetails?.shipmentStops || [])].sort(
    (a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0)
  );
  if (!stops.length) {
    if (!silent) notify.error({
      title: "Refresh failed",
      message: "Stops are required to refresh rates.",
    });
    return { success: false, reason: "Stops are required to refresh rates." };
  }

  const authToken = Cookies.get("authToken");
  const oc = shipmentDetails?.officeCode || officeCode;
  if (!authToken || !oc) {
    if (!silent) notify.error({
      title: "Refresh failed",
      message: "Missing credentials to refresh rates.",
    });
    return { success: false, reason: "Missing credentials to refresh rates." };
  }
  if (!shipmentDetails?.shipmentNumber) {
    if (!silent) notify.error({
      title: "Refresh failed",
      message: "Shipment number is required for rate shopping.",
    });
    return { success: false, reason: "Shipment number is required for rate shopping." };
  }
  if (!shipmentDetails?.shipmentNumber) {
    notify.error({
      title: "Refresh failed",
      message: "Shipment number is required for rate shopping.",
    });
    return;
  }

  setIsRefreshingRates(true);
  if (!silent) notify.info({
    title: "Reloading rates",
    message: "Fetching latest rates and updating charges...",
  });
  try {
    const assignedRates = await fetchAssignedCarrierRates({ shipmentDetails, officeCode: oc, authToken });
    const breakdown = assignedRates?.breakdown || [];
    const assignedCode = normalize(
      shipmentDetails?.carrierOfficeCode?.partyOfficeCode || shipmentDetails?.carrierCode
    );
    const assignedName = normalize(shipmentDetails?.carrierName);

     // Find BASE and FSC from adjusted/rate breakdown
    const findCharge = (type) =>
      breakdown.find((b) => normalize(b?.type) === normalize(type));
    const baseCharge = findCharge("BASE");
    const fscCharge = findCharge("FSC");

    // Find SOC from accBreakdown
    const accBreakdown = assignedRates?.accBreakdown || [];
    const socRate = accBreakdown.find((b) => normalize(b?.type) === "SOC");

    // SOC applies only to intermediate stops (exclude first pickup and last dropoff)
    const intermediateStops = stops.length - 2;
    // Total SOC = rate per stop × intermediate stops. null if not applicable
    const socAmount = socRate && intermediateStops > 0
    ? socRate.cost * intermediateStops
    : null;

    console.log("[Refresh Rates]", {
      officeCode: oc,
      assignedCarrier: {
        code: assignedCode || null,
        name: assignedName || null,
      },
      baseCharge: baseCharge?.cost ?? null,
      fscCharge: fscCharge?.cost ?? null,
      socCharge: socAmount ?? null,
      rawBase: baseCharge || null,
      rawFsc: fscCharge || null,
      rawSoc: socRate || null,
      totalStops: stops.length,
      intermediateStops,
    });

    // Seeded from the live rows, which may include an unsaved "Select charge" placeholder — a
    // rate refresh must not be what finally persists it (LGX-1098).
    const updatedCharges = withoutUncodedCharges(shipmentDetails?.charges);
    const upsertCharge = (code, amount, currency, chargeNameFallback, appliedRate, rateBasis, quantity) => {
      if (amount == null) return;
      const idx = updatedCharges.findIndex(
        (c) => normalize(c?.code) === normalize(code)
      );
      if (idx >= 0) {
        updatedCharges[idx] = {
          ...updatedCharges[idx],
          amount,
          currency: currency || updatedCharges[idx].currency || shipmentDetails?.currency,
          ...(appliedRate != null && { appliedRate }),
          ...(rateBasis != null && { rateBasis }),
          ...(quantity != null && { quantity }),
        };
      } else {
        updatedCharges.push({
          code,
          chargeName: chargeNameFallback,
          amount,
          currency: currency || shipmentDetails?.currency,
          type: "COST",
          chargeApprovalStatus: "PREAPPROVED",
          appliedRate: appliedRate ?? null,
          rateBasis: rateBasis ?? null,
          quantity: quantity ?? null,
        });
      }
    };

    const baseCurrency =
      baseCharge?.currency?.currencyCode || shipmentDetails?.currency;
    const fscCurrency =
      fscCharge?.currency?.currencyCode || shipmentDetails?.currency;
    const socCurrency =
      socRate?.currency || shipmentDetails?.currency;

    // BASE and FSC from rate API, SOC only if intermediate stops exist
    upsertCharge("BASE", baseCharge?.cost, baseCurrency, "Base Charge", baseCharge?.appliedRate, baseCharge?.basis, baseCharge?.quantity);
    upsertCharge("FSC", fscCharge?.cost, fscCurrency, "Fuel Surcharge", fscCharge?.appliedRate, fscCharge?.basis, fscCharge?.quantity);
    upsertCharge("SOC", socAmount, socCurrency, "Stop-off Charge", socRate?.cost, socRate?.basis, socRate?.quantity != null ? socRate.quantity : intermediateStops > 0 ? intermediateStops : null);

    const hasAutoUpdates =
      baseCharge?.cost != null || fscCharge?.cost != null || socAmount != null; //include SOC

    if (hasAutoUpdates && updatedCharges.length) {
      const payloadCharges = updatedCharges.map((charge) => {
        let chargeName = charge.chargeName;
        if (chargeName === "BASE") chargeName = "Base Charge";
        if (chargeName === "FSC") chargeName = "Fuel Surcharge";
        return {
          shipment: { id: shipmentDetails.id },
          amount: charge.amount,
          currency: charge.currency || shipmentDetails?.currency,
          code: charge.code,
          chargeName,
          chargeApprovalStatus: charge.chargeApprovalStatus,
          invoiceNumber: charge.invoiceNumber,
          type: charge.type || "COST",
          appliedRate: charge.appliedRate ?? null,
          rateBasis: charge.rateBasis ?? null,
          quantity: charge.quantity ?? null,
          ...(charge.id && { id: charge.id }),
          ...(charge.version && { version: charge.version }),
        };
      });

      try {
        // Capture PUT response to get updated totalCharge from backend
        const putResponse= await httpClient.put(
          `/api/shipment-charges-update/shipmentNumber/${shipmentDetails.shipmentNumber}/REORDER_STOPS_PARTIAL_RESET`,
          {
            shipment: {},
            shipmentCharges: payloadCharges,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "Office-Code": oc,
            },
          }
        );

          const shipmentFromResponse = putResponse?.data?.[0]?.shipment;

        if (shipmentFromResponse) {
          // Use shipmentFromResponse for totalCharge (backend calculated)
          // Use updatedCharges for charges (full objects, no bare IDs)
          setShipmentDetails?.({
            ...shipmentFromResponse,  
            charges: updatedCharges, 
          });
        } else {
          // Fallback if response doesn't contain shipment
          setShipmentDetails?.((prev) =>
            prev ? { ...prev, charges: updatedCharges } : prev
          );
        }
      } catch (err) {
        console.error("Charge update failed:", err);
        if (!silent) notify.error({
          title: "Refresh failed",
          message: "Could not update shipment charges.",
        });
        throw err;
      }
    }

    if (!silent) notify.success({
      title: "Refresh complete",
      message: "Rates reloaded and charges updated.",
    });
    return { success: true };
  } catch (error) {
    console.error("Rate refresh failed:", error);
    if (!silent) notify.error({
      title: "Refresh failed",
      message: "Unable to reload rates right now.",
    });
    return { success: false, reason: "Unable to reload rates right now." };
  } finally {
    setIsRefreshingRates(false);
  }
};

export default refreshRatesAndUpdateCharges;
